import { Analytics } from '@cobliteam/analytics';
import {
  type CrmReportProblemTicket,
  type CrmReportProblemTicketsDto,
  CrmRequester,
  type CrmSchedulingTicketSummaryDto,
  type CrmTicketDto,
  type CrmTicketsDto,
  FuscaVehiclesRequester,
  type LastAssociationDeviceVehicle,
  type UpdateCrmSchedulingFormDto,
} from '@cobliteam/api-lib';
import GraphQLCrmRequester from '@cobliteam/api-lib/src/dashboard/crm/GraphQLCrmRequester';
import { type TicketDeviceRequest, type UpdateContactTrainingInfoRequestDto, UserTrainingMediaActionType, type UUID } from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { type UseQueryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import useTrainingVideo from '../useTrainingVideo';
import useUser from '../useUser';

export const CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY = 'CRM_TICKETS_SCHEDULE_INSTALLATION';
export const CRM_SCHEDULING_SUMMARY_QUERY_KEY = 'CRM_SCHEDULING_SUMMARY_QUERY_KEY';

export const CRM_SHIPPING_QUERY_KEY = 'CRM_TICKETS_SHIPPING';
export const CRM_SCHEDULING_QUERY_KEY = 'CRM_TICKETS_SCHEDULING';
export const CRM_DEVOLUTION_QUERY_KEY = 'CRM_TICKETS_DEVOLUTION';

const env = Environment.getInstance().getEnvironment();

// Lazy instantiation to avoid circular dependency issues
const getCrmRequester = () => new CrmRequester({ host: env.apiHost });
const getFuscaVehiclesRequester = () =>
  new FuscaVehiclesRequester({
    host: env.apiHost,
  });

interface UpdateSchedulingTicketProps {
  ticketId: string;
  data: CrmTicketDto;
}

const useCrm = () => {
  const { getSession } = useUser();
  const fleetId = getSession().fleet.id;
  const userId = getSession().user.id;

  const queryClient = useQueryClient();
  const graphgQLRequester = new GraphQLCrmRequester();

  const { startUserTrainingMedia, finishUserTrainingMedia } = useTrainingVideo();
  const { mutate: updateStartUserTrainingMedia } = startUserTrainingMedia();
  const { mutate: updateFinishUserTrainingMedia } = finishUserTrainingMedia();

  const getSchedulingTickets = ({ options }: { options?: Omit<UseQueryOptions<CrmTicketsDto>, 'queryKey' | 'queryFn'> } = {}) => {
    return useQuery<CrmTicketsDto>({
      queryKey: [CRM_SCHEDULING_QUERY_KEY, fleetId],
      queryFn: () => getCrmRequester().getSchedulingTickets(),
      ...options,
    });
  };

  const updateSchedulingTicket = ({ ticketId, data }: UpdateSchedulingTicketProps) => {
    queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
      if (!old) return old;

      const tickets = old.tickets.map((item) => {
        if (item.id === ticketId) {
          return {
            ...item,
            ...data,
          };
        }
        return item;
      });
      return {
        tickets,
        total: old.total,
      };
    });
  };

  const getNationalHolidays = () => {
    return useQuery<Date[]>({
      queryKey: ['NATIONAL-HOLIDAYS'],
      queryFn: async () => await graphgQLRequester.getSchedulingHolidays(),
    });
  };

  const getShippingTickets = () => {
    return useQuery<CrmTicketsDto>({
      queryKey: [CRM_SHIPPING_QUERY_KEY, fleetId],
      queryFn: () => getCrmRequester().getShippingTickets(),
    });
  };

  const getSchedulingTicketsSummary = ({ options }: { options?: Omit<UseQueryOptions<CrmSchedulingTicketSummaryDto>, 'queryKey' | 'queryFn'> } = {}) => {
    return useQuery<CrmSchedulingTicketSummaryDto>({
      queryKey: [CRM_SCHEDULING_SUMMARY_QUERY_KEY, fleetId],
      queryFn: () => getCrmRequester().getSchedulingTicketsSummary(),
      ...options,
    });
  };

  const getDevolutionTickets = () => {
    return useQuery<CrmTicketsDto>({
      queryKey: [],
      queryFn: () => getCrmRequester().getDevolutionTickets(),
    });
  };

  const getDevicesLastAssociations = (devicesIds: any, { enabled = true, refetchOnMount = false, ...options }: Partial<UseQueryOptions<LastAssociationDeviceVehicle[]>>) =>
    useQuery<LastAssociationDeviceVehicle[]>({
      queryKey: [CRM_DEVOLUTION_QUERY_KEY, 'LAST_ASSOCIATIONS', devicesIds],
      queryFn: () => getCrmRequester().getLastAssociations(devicesIds),
      ...options,
      enabled,
      refetchOnMount,
    });

  const notResponsibleForInstallation = (ticketId: string) => {
    return useMutation<string, undefined>({
      mutationFn: async () => getCrmRequester().notResponsible(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                installationInformation: {
                  ...ticket.installationInformation,
                  notResponsible: [userId],
                },
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
    });
  };

  const responsibleForInstallation = (ticketId: string) => {
    return useMutation<string, undefined>({
      mutationFn: async () => getCrmRequester().responsible(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                installationInformation: {
                  ...ticket.installationInformation,
                  responsibleUser: {
                    ...ticket.installationInformation.responsibleUser,
                    id: userId,
                  },
                },
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
    });
  };

  const confirmInstallation = (ticketId: string) => {
    return useMutation<string, undefined>({
      mutationFn: () => getCrmRequester().confirmInstallation(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                installationInformation: {
                  ...ticket.installationInformation,
                  installationConfirmedAt: new Date().toISOString(),
                },
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
      onError: () => {
        Alert.error('Erro ao confirmar a instalação.');
      },
      onSuccess: () => {
        Alert.success('Instalação confirmada com sucesso!');
      },
    });
  };

  const cancelInstallation = (ticketId: string) => {
    return useMutation<undefined, undefined>({
      mutationFn: async () => getCrmRequester().cancelInstallation(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                pipelineStage: '34178455',
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
      onError: () => {
        Alert.error('Erro ao cancelar a instalação.');
      },
    });
  };

  const rateInstallation = (ticketId: string) => {
    return useMutation({
      mutationFn: () => getCrmRequester().rateInstallation(ticketId),
      onMutate: async () => {
        const previousScheduleTickets: CrmTicketsDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        queryClient.setQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId], (old: CrmTicketsDto | undefined) => {
          if (!old) return old;

          const tickets = old.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                installationInformation: {
                  ...ticket.installationInformation,
                  ratedAt: new Date().toISOString(),
                },
              };
            }
            return ticket;
          });

          return {
            ...old,
            tickets,
          };
        });

        return { previousScheduleTickets };
      },
      onError: () => {
        Alert.error('Erro ao avaliar a instalação.');
      },
      onSuccess: () => {
        Alert.success('Avaliação enviada com sucesso!');
      },
    });
  };

  const useGetTicket = (deviceId: string, options?: Partial<UseQueryOptions<any>>) => {
    return useQuery<CrmReportProblemTicketsDto>({
      queryKey: [CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, deviceId, 'TICKETS'],
      queryFn: () => getCrmRequester().searchTicket(deviceId),
      ...options,
    });
  };

  const useCreateTicket = (deviceId: string) => {
    return useMutation({
      mutationFn: (ticketRequest: TicketDeviceRequest) => {
        return getCrmRequester().reportProblem(deviceId, ticketRequest);
      },
      onMutate: async (newTicket) => {
        const KEY_QUERY = [CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, deviceId, 'TICKETS'];

        await queryClient.cancelQueries({ queryKey: KEY_QUERY });
        const previousTickets: CrmReportProblemTicketsDto | undefined = queryClient.getQueryData(KEY_QUERY);

        queryClient.setQueryData<CrmReportProblemTicketsDto | undefined>(KEY_QUERY, (data: any) => {
          if (!data) return data;

          const tempTicket: CrmReportProblemTicket = {
            id: 'Aguardando Nº do ticket...',
            createdAt: new Date().toISOString(),
            properties: {
              subject: newTicket.problem,
            },
          };

          return {
            ticket: {
              total: data.tickets?.length ? data.total + 1 : 1,
              results: [tempTicket, ...(data?.tickets ?? [])],
            },
          };
        });

        return { previousTickets };
      },
      onError: (_, __, context) => {
        queryClient.setQueryData([CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, deviceId, 'TICKETS'], context?.previousTickets);
      },
    });
  };

  const useGetDealIdHubspot = () => {
    return useQuery({
      queryKey: [CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, 'DEAL_ID_HUBSPOT', fleetId],
      queryFn: () => getCrmRequester().getHubpotDealId(fleetId),
      enabled: true,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  const useUpdateSchedulingTicketForm = () => {
    return useMutation<CrmTicketDto, undefined, UpdateCrmSchedulingFormDto>({
      mutationFn: async (formRequest: UpdateCrmSchedulingFormDto) => {
        return getCrmRequester().updateSchedulingTicketForm(formRequest);
      },
      onMutate: async () => {
        const ticket: CrmTicketDto | undefined = queryClient.getQueryData([CRM_SCHEDULING_QUERY_KEY, fleetId]);

        return { ticket };
      },
      onSuccess: () => {
        Alert.success('Formulário enviado com sucesso!', {
          closeButton: true,
        });
        queryClient.invalidateQueries({ queryKey: ['CRM_ORDERS'] });
      },
      onError: () => {
        Alert.error('Erro ao agendar a instalação.');
      },
    });
  };

  const useDisassociateDeviceFromVehicle = useMutation<void, unknown, UUID>({
    mutationFn: (vehicleId: UUID) => getFuscaVehiclesRequester().removeDevice(vehicleId),
    onSuccess: () => {
      queryClient.refetchQueries({
        queryKey: ['DEVICES'],
      });
    },
  });

  const useUpdateContactTrainings = (trainingMediaId: UUID, action: UserTrainingMediaActionType) =>
    useMutation<any, unknown, UpdateContactTrainingInfoRequestDto>({
      mutationFn: (userTrainings: UpdateContactTrainingInfoRequestDto) => graphgQLRequester.updateContactTrainings(userTrainings),
      onSuccess: async (data: any) => {
        if (data.errors) return;

        if (action == UserTrainingMediaActionType.START) {
          await updateStartUserTrainingMedia(trainingMediaId);
          Analytics.track('Started training');
        } else if (action == UserTrainingMediaActionType.FINISH) {
          Analytics.track('Finished training');
          await updateFinishUserTrainingMedia(trainingMediaId);
        }
      },
    });

  return {
    getSchedulingTickets,
    updateSchedulingTicket,
    getShippingTickets,
    getSchedulingTicketsSummary,
    getNationalHolidays,
    notResponsibleForInstallation,
    responsibleForInstallation,
    confirmInstallation,
    cancelInstallation,
    rateInstallation,
    useGetTicket,
    useCreateTicket,
    useGetDealIdHubspot,
    getDevolutionTickets,
    getDevicesLastAssociations,
    useDisassociateDeviceFromVehicle,
    useUpdateSchedulingTicketForm,
    useUpdateContactTrainings,
  };
};

export default useCrm;
